import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { router } from 'expo-router';
import {
  Home,
  Map,
  Newspaper,
  HelpCircle,
  ChevronRight,
  X,
  type LucideIcon,
} from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme, SPACING, BORDER_RADIUS, ELEVATION, TYPOGRAPHY } from '../../theme';
import { useUIStore } from '../../store/uiStore';
import { useStructuresStore } from '../../store/structuresStore';
import { useTabBarClearance } from '@hooks/useTabBarClearance';
import { useEnsureScout } from '@hooks/useEnsureScout';
import { useOverlayFade } from '@hooks/useAnimations';
import { resolvePropertyTarget } from '@hooks/useOpenProperty';
import { BrandTile } from '../common';
import { CURSOR_POINTER, FROSTED_GLASS_BLUR } from '@utils/webStyles';

interface MapPathsOverlayProps {
  /** Focuses the map's address search bar (searchBarRef lives in
   *  app/(tabs)/index.tsx — this is a callback, not the ref itself, so the
   *  overlay doesn't need to know how the map screen wires its handles). */
  onEvaluate: () => void;
  /** True while the map's first structures load is still in flight —
   *  mirrors the MapLegend/MapLoadingOverlay gate so the call-to-action
   *  doesn't sit over a dimmed, half-loaded map. */
  mapLoading: boolean;
}

/**
 * First-visit call to action over the map (client ask, Aug 2026 — "a call to
 * action whenever people come to the site"): three semi-transparent cards
 * answering what to do, how, and why, shown every visit until the visitor
 * engages a card or dismisses the block, then never again on this device.
 * The only guidance surface — the old sheet (formerly GuidanceSheet.tsx) is
 * retired; the Guidance tab re-summons this overlay instead of opening
 * anything (see uiStore.requestMapPathsOverlay).
 *
 * Modeled on MapSignInOverlay (absolute, self-gating map overlay) for
 * positioning and on CreatePostCoachmark (fade-in Animated.View + persisted
 * AsyncStorage flag) for the show/dismiss lifecycle. No `user` gate:
 * signed-out visitors are the entire point.
 *
 * Skips its automatic first-visit show when `uiStore.arrivedViaPlanLink` is
 * set (a `/plan/<id>` share link routed this visit) — the plan viewer owns
 * the screen instead. A Guidance-tab reopen still works either way.
 */
export function MapPathsOverlay({ onEvaluate, mapLoading }: MapPathsOverlayProps) {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const tabBarClearance = useTabBarClearance();
  const tabBarVisible = useUIStore((s) => s.tabBarVisible);
  const activeSheet = useUIStore((s) => s.activeSheet);
  const hasSeenMapPaths = useUIStore((s) => s.hasSeenMapPaths);
  const markMapPathsEngaged = useUIStore((s) => s.markMapPathsEngaged);
  // Session-only re-open signal, set by the Guidance tab. Bypasses
  // hasSeenMapPaths on purpose: tapping Guidance re-summons the overlay even
  // on a device that already engaged it once.
  const mapPathsOverlayRequested = useUIStore((s) => s.mapPathsOverlayRequested);
  const clearMapPathsOverlayRequest = useUIStore((s) => s.clearMapPathsOverlayRequest);
  // A /plan/<id> share link routed this visit here — the plan someone sent
  // is the reason the visitor is on the map, not REDI itself, so this one
  // visit skips the automatic welcome. A deliberate Guidance-tab reopen
  // still works (mapPathsOverlayRequested is checked first, below) — that's
  // the visitor asking, not the app assuming.
  const arrivedViaPlanLink = useUIStore((s) => s.arrivedViaPlanLink);
  // The plan-map placement editor covers the map with a moderator's image
  // and three draggable corner handles, and this block sits over the middle
  // of that map catching clicks even at opacity 0 — a corner under it can't
  // be grabbed (found Sept 18 2026 alongside the marker-pane fix). Same gate
  // MapLegend uses for the same reason.
  const placingOverlayId = useUIStore((s) => s.placingOverlayId);
  const selectedStructure = useStructuresStore((s) => s.selectedStructure);
  const { ensureScoutThen } = useEnsureScout();

  const [visible, setVisible] = useState(false);
  const engagedRef = useRef(false);

  // Computed here, above the fade, rather than only as the early returns
  // below: every one of these unmounts the Animated.View, and an unmount
  // stops a running fade dead (see useOverlayFade). The fade has to know
  // about the gates or it comes back frozen at whatever opacity it reached.
  const gated =
    !tabBarVisible ||
    activeSheet !== null ||
    Boolean(selectedStructure) ||
    mapLoading ||
    Boolean(placingOverlayId);
  const { opacity, pointerEvents, fadeOut } = useOverlayFade(visible && !gated);

  // Show when hydration resolves to "never engaged" (mirrors
  // CreatePostCoachmark so returning devices don't see a flash before the
  // async AsyncStorage read comes back) OR the Guidance tab asks for a
  // reopen. Either trigger also clears engagedRef: without that, a second
  // reopen on an already-mounted overlay would find engage() a no-op from
  // the first dismissal and the cards would render inert.
  useEffect(() => {
    if (mapPathsOverlayRequested) {
      engagedRef.current = false;
      setVisible(true);
      return;
    }
    if (arrivedViaPlanLink) return;
    if (hasSeenMapPaths === false) {
      engagedRef.current = false;
      setVisible(true);
    }
  }, [hasSeenMapPaths, mapPathsOverlayRequested, arrivedViaPlanLink]);

  // Card taps and the dismiss X funnel through here: persist the flag, clear
  // any pending reopen request, run the card's own side effect (focus/
  // navigate) immediately, then fade the block out. The side effect doesn't
  // wait on the animation — for the two navigating cards the screen is on
  // its way out anyway, and for "Evaluate" the keyboard coming up and the
  // card fading out can happen at once.
  const engage = useCallback(
    (sideEffect?: () => void) => {
      if (engagedRef.current) return;
      engagedRef.current = true;
      markMapPathsEngaged();
      clearMapPathsOverlayRequest();
      sideEffect?.();
      fadeOut(() => setVisible(false));
    },
    [fadeOut, markMapPathsEngaged, clearMapPathsOverlayRequest]
  );

  if (!visible) return null;
  // Live gates re-checked every render (not just at hydration time): a sheet
  // opening, a structure getting selected, or the map still loading should
  // hide the block immediately, the same way MapSignInOverlay disappears.
  if (gated) return null;

  return (
    <View
      testID="map-paths-overlay"
      pointerEvents="box-none"
      style={[
        styles.root,
        { top: insets.top + SPACING.md, bottom: tabBarClearance || insets.bottom + SPACING.md },
      ]}>
      <View style={styles.centerWrap} pointerEvents="box-none">
        {/* pointerEvents rides with the opacity: the block is clickable only
            once the fade-in has actually finished. As a prop, not a style —
            RN-web compiles the prop into real pointer-events rules, and its
            inline-style path doesn't handle pointerEvents at all. */}
        <Animated.View
          testID="map-paths-block"
          pointerEvents={pointerEvents}
          style={[styles.block, { opacity }]}>
          <View
            style={[
              styles.card,
              {
                // Web: frosted glass — a light-alpha tint plus a real
                // backdrop blur (FROSTED_GLASS_BLUR) so the card reads over
                // an unpredictable basemap without needing much opacity.
                // Native has no blur, so it leans on a near-opaque surface
                // instead (surfaceTranslucentSolid).
                backgroundColor:
                  Platform.OS === 'web'
                    ? colors.surfaceTranslucent
                    : colors.surfaceTranslucentSolid,
                borderColor: colors.border,
              },
              FROSTED_GLASS_BLUR,
              ELEVATION.float,
            ]}>
            <Pressable
              onPress={() => engage()}
              accessibilityRole="button"
              accessibilityLabel="Dismiss"
              testID="map-paths-dismiss"
              hitSlop={8}
              style={[styles.dismiss, CURSOR_POINTER]}>
              <X size={16} color={colors.textSecondary} />
            </Pressable>

            <Text style={[styles.title, { color: colors.text }]}>Welcome to REDI</Text>
            <Text style={[styles.tagline, { color: colors.textSecondary }]}>
              Community Wildfire Defense
            </Text>
            <BrandTile variant="welcome" style={styles.brandTile} />

            {/* Cards mirror the navigation one-to-one (client ask, Aug 2026):
                Property / Community / News, same order and icons as ALL_TABS. */}
            <View style={styles.rows}>
              <PathRow
                icon={Home}
                title="Property"
                body="Post photos to show home hardening for a specific property."
                testID="map-paths-evaluate"
                onPress={() =>
                  engage(() => {
                    // Same behavior as the Property tab, but the search-focus
                    // fallback stays the direct onEvaluate ref call: a
                    // store-routed focus lands in an effect, outside the tap
                    // gesture, and mobile web won't raise the keyboard there.
                    const target = resolvePropertyTarget();
                    if (target.kind === 'href') router.push(target.href);
                    else if (target.kind === 'picker')
                      useUIStore.getState().openSheet('propertyPicker');
                    else onEvaluate();
                  })
                }
              />
              <PathRow
                icon={Map}
                title="Community"
                body="Show progress and needs in this area."
                testID="map-paths-observe"
                onPress={() => engage(() => ensureScoutThen(() => router.push('/post/create')))}
              />
              <PathRow
                icon={Newspaper}
                title="News"
                body="Learn about plans, progress and resources in your community."
                testID="map-paths-news"
                onPress={() => engage(() => router.push('/(tabs)/feed'))}
              />
              <PathRow
                icon={HelpCircle}
                title="How REDI works"
                body="A short tour of what REDI does and how to use it."
                testID="map-paths-how"
                onPress={() => engage(() => useUIStore.getState().openSheet('howItWorks'))}
                isLast
              />
            </View>
          </View>
        </Animated.View>
      </View>
    </View>
  );
}

interface PathRowProps {
  icon: LucideIcon;
  title: string;
  body: string;
  testID: string;
  onPress: () => void;
  isLast?: boolean;
}

function PathRow({ icon: Icon, title, body, testID, onPress, isLast }: PathRowProps) {
  const { colors } = useTheme();
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={title}
      testID={testID}
      style={[
        styles.row,
        !isLast && {
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderBottomColor: colors.border,
        },
        CURSOR_POINTER,
      ]}>
      <View style={styles.rowIcon}>
        <Icon size={18} color={colors.primary} />
      </View>
      <View style={styles.rowText}>
        <Text style={[styles.rowTitle, { color: colors.text }]}>{title}</Text>
        <Text style={[styles.rowBody, { color: colors.textSecondary }]}>{body}</Text>
      </View>
      <ChevronRight size={16} color={colors.textTertiary} />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: {
    position: 'absolute',
    left: 0,
    right: 0,
    // Above the search overlay (100), below the create coachmark (999) —
    // see app/(tabs)/index.tsx.
    zIndex: 105,
  },
  centerWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: SPACING.md,
  },
  block: {
    width: '100%',
    maxWidth: 420,
    alignSelf: 'center',
  },
  card: {
    borderRadius: BORDER_RADIUS.xl,
    borderWidth: 1,
    padding: SPACING.lg,
  },
  dismiss: {
    position: 'absolute',
    top: SPACING.sm,
    right: SPACING.sm,
    padding: SPACING.xs,
  },
  title: {
    ...TYPOGRAPHY.h4,
    paddingRight: SPACING.lg,
  },
  tagline: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 2,
    marginBottom: SPACING.sm,
  },
  brandTile: {
    marginTop: SPACING.xs,
    marginBottom: SPACING.sm,
  },
  rows: {
    marginTop: SPACING.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: SPACING.smd,
    gap: SPACING.smd,
  },
  rowIcon: {
    marginTop: 2,
  },
  rowText: {
    flex: 1,
  },
  rowTitle: {
    ...TYPOGRAPHY.label,
    marginBottom: 2,
  },
  rowBody: {
    ...TYPOGRAPHY.bodySmall,
  },
});
